import type { CustomObjectField } from '@kizen/api/custom-object';
import type {
  FieldCategory,
  GetAllPipelineFieldsParams,
  SearchFieldsOptions,
} from '@kizen/api/pipeline';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithRequired } from './types';

import {
  getAllPipelineObjectFields,
  getPipelineObjectCategories,
  getPipelineObjectFields,
} from '@kizen/api/pipeline';
import { getResource } from './resource';
import { curry1 } from './utils';

type AllCustomObjectFieldsQueryOptions<TData = CustomObjectField[]> = Omit<
  AllPipelineObjectFieldsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  params?: GetAllPipelineFieldsParams;
};

type PipelineObjectFieldsQueryOptions<TData = CustomObjectField[]> = Omit<
  PipelineObjectFieldsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  body?: SearchFieldsOptions['body'];
  params?: SearchFieldsOptions['params'];
};

type AllPipelineObjectFieldsQuery<TData = CustomObjectField[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    DefaultError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['pipelines', string, 'fields', 'settings-search']>,
        GetAllPipelineFieldsParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type PipelineObjectCategoriesQuery<TData = FieldCategory[]> = WithRequired<
  QueryObserverOptions<
    FieldCategory[],
    DefaultError,
    TData,
    FieldCategory[],
    [Resource<Readonly<['pipelines', string, 'categories']>>]
  >,
  'queryFn' | 'queryKey'
>;

type PipelineObjectFieldsQuery<TData = CustomObjectField[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    DefaultError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['pipelines', string, 'fields', 'search']>,
        SearchFieldsOptions['params'],
        SearchFieldsOptions['body']
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const allPipelineObjectFieldsQuery = <TData = CustomObjectField[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: AllCustomObjectFieldsQueryOptions<TData> = {}
): AllPipelineObjectFieldsQuery<TData> => {
  const path = [
    'pipelines',
    customObjectId,
    'fields',
    'settings-search',
  ] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getAllPipelineObjectFields(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

export const pipelineObjectCategoriesQuery = <TData = FieldCategory[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: Omit<
    PipelineObjectCategoriesQuery<TData>,
    'queryFn' | 'queryKey'
  > = {}
): PipelineObjectCategoriesQuery<TData> => {
  const path = ['pipelines', customObjectId, 'categories'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getPipelineObjectCategories(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const pipelineObjectFieldsQuery = <TData = CustomObjectField[]>(
  axios: AxiosInstance,
  customObjectId: string,
  options: PipelineObjectFieldsQueryOptions<TData> = {}
): PipelineObjectFieldsQuery<TData> => {
  const path = ['pipelines', customObjectId, 'fields', 'search'] as const;
  const { body, params, ...queryOptions } = options;
  const resource = getResource(path, { body, params, method: 'POST' });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getPipelineObjectFields(axios, qk.path[1], {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

class Queries {
  public allPipelineObjectFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof allPipelineObjectFieldsQuery<TData>>>
  ) => ReturnType<typeof allPipelineObjectFieldsQuery<TData>>;
  public pipelineObjectCategoriesQuery: <TData = FieldCategory[]>(
    ...args: Drop<Parameters<typeof pipelineObjectCategoriesQuery<TData>>>
  ) => ReturnType<typeof pipelineObjectCategoriesQuery<TData>>;
  public pipelineObjectFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof pipelineObjectFieldsQuery<TData>>>
  ) => ReturnType<typeof pipelineObjectFieldsQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.allPipelineObjectFieldsQuery = curry1(allPipelineObjectFieldsQuery)(
      axios
    );
    this.pipelineObjectCategoriesQuery = curry1(pipelineObjectCategoriesQuery)(
      axios
    );
    this.pipelineObjectFieldsQuery = curry1(pipelineObjectFieldsQuery)(axios);
  }
}

export type PipelineObjectQueries = Queries;

export const defPipelineObjectQueries = (
  axios: AxiosInstance
): PipelineObjectQueries => {
  return new Queries(axios);
};
